"use client";

import { c as _c } from "react/compiler-runtime";
import { useChat, type UseChatHelpers } from "@ai-sdk/react";
import { Presence } from "@radix-ui/react-presence";
import { DefaultChatTransport, type Tool, type UIToolInvocation } from "ai";
import { buttonVariants } from "fumadocs-ui/components/ui/button";
import { Loader2, MessageCircleIcon, RefreshCw, SearchIcon, Send, X } from "lucide-react";
import { type ComponentProps, createContext, type ReactNode, type SyntheticEvent, use, useEffect, useEffectEvent, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/cn";
import { useDocsI18n } from "@/lib/i18n";
import { Markdown } from "../markdown";
import type { ChatUIMessage, SearchTool } from "@/app/api/chat/route";
const Context = createContext<{
  open: boolean;
  setOpen: (open: boolean) => void;
  chat: UseChatHelpers<ChatUIMessage>;
} | null>(null);
export function AISearchPanelHeader(t0) {
  const $ = _c(30);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const {
    setOpen
  } = useAISearchContext();
  const {
    t
  } = useDocsI18n();
  let t1;
  if ($[3] !== className) {
    t1 = cn("sticky top-0 flex items-start gap-2 border rounded-xl bg-fd-secondary text-fd-secondary-foreground shadow-sm", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t) {
    t2 = t("aiSearch.title");
    $[5] = t;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t2) {
    t3 = <p className="text-sm font-medium mb-2">{t2}</p>;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t) {
    t4 = t("aiSearch.disclaimer");
    $[9] = t;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t4) {
    t5 = <p className="text-xs text-fd-muted-foreground">{t4}</p>;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t3 || $[14] !== t5) {
    t6 = <div className="px-3 py-2 flex-1">{t3}{t5}</div>;
    $[13] = t3;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t) {
    t7 = t("aiSearch.close");
    $[16] = t;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = cn(buttonVariants({
      size: "icon-sm",
      color: "ghost",
      className: "text-fd-muted-foreground rounded-full"
    }));
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== setOpen) {
    t9 = () => setOpen(false);
    $[19] = setOpen;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <X />;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t7 || $[23] !== t9) {
    t11 = <button aria-label={t7} tabIndex={-1} className={t8} onClick={t9}>{t10}</button>;
    $[22] = t7;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== props || $[26] !== t1 || $[27] !== t11 || $[28] !== t6) {
    t12 = <div className={t1} {...props}>{t6}{t11}</div>;
    $[25] = props;
    $[26] = t1;
    $[27] = t11;
    $[28] = t6;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  return t12;
}
export function AISearchInputActions() {
  const $ = _c(17);
  const {
    messages,
    status,
    setMessages,
    regenerate
  } = useChatContext();
  const {
    t
  } = useDocsI18n();
  const isLoading = status === "streaming";
  if (messages.length === 0) {
    return null;
  }
  let t0;
  if ($[0] !== isLoading || $[1] !== messages || $[2] !== regenerate || $[3] !== t) {
    t0 = !isLoading && messages.at(-1)?.role === "assistant" && <button type="button" className={cn(buttonVariants({
      color: "secondary",
      size: "sm",
      className: "rounded-full gap-1.5"
    }))} onClick={() => regenerate()}><RefreshCw className="size-4" />{t("aiSearch.retry")}</button>;
    $[0] = isLoading;
    $[1] = messages;
    $[2] = regenerate;
    $[3] = t;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn(buttonVariants({
      color: "secondary",
      size: "sm",
      className: "rounded-full"
    }));
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== setMessages) {
    t2 = () => setMessages([]);
    $[6] = setMessages;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t) {
    t3 = t("aiSearch.clearChat");
    $[8] = t;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t1 || $[11] !== t2 || $[12] !== t3) {
    t4 = <button type="button" className={t1} onClick={t2}>{t3}</button>;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t0 || $[15] !== t4) {
    t5 = <>{t0}{t4}</>;
    $[14] = t0;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
}
const StorageKeyInput = "__ai_search_input";
export function AISearchInput(props) {
  const $ = _c(30);
  const {
    status,
    sendMessage,
    stop
  } = useChatContext();
  const {
    t
  } = useDocsI18n();
  const [input, setInput] = useState(_temp);
  const isLoading = status === "streaming" || status === "submitted";
  let t0;
  if ($[0] !== input || $[1] !== sendMessage) {
    t0 = e => {
      e?.preventDefault();
      const message = input.trim();
      if (message.length === 0) {
        return;
      }
      sendMessage({
        role: "user",
        parts: [{
          type: "data-client",
          data: {
            location: location.href
          }
        }, {
          type: "text",
          text: message
        }]
      });
      setInput("");
      window.localStorage.removeItem(StorageKeyInput);
    };
    $[0] = input;
    $[1] = sendMessage;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const onStart = t0;
  let t1;
  let t2;
  if ($[3] !== isLoading) {
    t1 = () => {
      if (isLoading) {
        document.getElementById("nd-ai-input")?.focus();
      }
    };
    t2 = [isLoading];
    $[3] = isLoading;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  let t3;
  if ($[6] !== props.className) {
    t3 = cn("flex items-start pe-2", props.className);
    $[6] = props.className;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== isLoading || $[9] !== t) {
    t4 = isLoading ? t("aiSearch.answering") : t("aiSearch.askPlaceholder");
    $[8] = isLoading;
    $[9] = t;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const t5 = status === "streaming" || status === "submitted";
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = e_0 => {
      setInput(e_0.target.value);
      window.localStorage.setItem(StorageKeyInput, e_0.target.value);
    };
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== onStart) {
    t7 = event => {
      if (!event.shiftKey && event.key === "Enter") {
        onStart(event);
      }
    };
    $[12] = onStart;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== input || $[15] !== t4 || $[16] !== t5 || $[17] !== t7) {
    t8 = <Input value={input} placeholder={t4} autoFocus={true} className="p-3" disabled={t5} onChange={t6} onKeyDown={t7} />;
    $[14] = input;
    $[15] = t4;
    $[16] = t5;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== input.length || $[20] !== isLoading || $[21] !== stop || $[22] !== t) {
    t9 = isLoading ? <button key="bn" type="button" className={cn(buttonVariants({
      color: "secondary",
      className: "transition-all rounded-full mt-2 gap-2"
    }))} onClick={stop}><Loader2 className="size-4 animate-spin text-fd-muted-foreground" />{t("aiSearch.abort")}</button> : <button key="bn" type="submit" className={cn(buttonVariants({
      color: "primary",
      className: "transition-all rounded-full mt-2"
    }))} disabled={input.length === 0}><Send className="size-4" /></button>;
    $[19] = input.length;
    $[20] = isLoading;
    $[21] = stop;
    $[22] = t;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== onStart || $[25] !== props || $[26] !== t3 || $[27] !== t8 || $[28] !== t9) {
    t10 = <form {...props} className={t3} onSubmit={onStart}>{t8}{t9}</form>;
    $[24] = onStart;
    $[25] = props;
    $[26] = t3;
    $[27] = t8;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  return t10;
}
function _temp() {
  return typeof window === "undefined" ? "" : window.localStorage.getItem(StorageKeyInput) ?? "";
}
function List(props) {
  const $ = _c(7);
  const containerRef = useRef(null);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      if (!containerRef.current) {
        return;
      }
      const callback = function callback() {
        const container = containerRef.current;
        if (!container) {
          return;
        }
        container.scrollTo({
          top: container.scrollHeight,
          behavior: "instant"
        });
      };
      const observer = new ResizeObserver(callback);
      callback();
      const element = containerRef.current?.firstElementChild;
      if (element) {
        observer.observe(element);
      }
      return () => {
        observer.disconnect();
      };
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] !== props.className) {
    t2 = cn("fd-scroll-container overflow-y-auto min-w-0 flex flex-col", props.className);
    $[2] = props.className;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== props || $[5] !== t2) {
    t3 = <div ref={containerRef} {...props} className={t2}>{props.children}</div>;
    $[4] = props;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function Input(props) {
  const $ = _c(15);
  const ref = useRef(null);
  let t0;
  let t1;
  let t2;
  let t3;
  if ($[0] !== props) {
    const shared = cn("col-start-1 row-start-1", props.className);
    t2 = "grid flex-1";
    t3 = <textarea id="nd-ai-input" {...props} className={cn("resize-none bg-transparent placeholder:text-fd-muted-foreground focus-visible:outline-none", shared)} />;
    t0 = ref;
    t1 = cn(shared, "break-all invisible");
    $[0] = props;
    $[1] = t0;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t0 = $[1];
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
  }
  let t4;
  if ($[5] !== props.value) {
    t4 = props.value?.toString() ?? "";
    $[5] = props.value;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = `${t4}\n`;
  let t6;
  if ($[7] !== t0 || $[8] !== t1 || $[9] !== t5) {
    t6 = <div ref={t0} className={t1}>{t5}</div>;
    $[7] = t0;
    $[8] = t1;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t2 || $[12] !== t3 || $[13] !== t6) {
    t7 = <div className={t2}>{t3}{t6}</div>;
    $[11] = t2;
    $[12] = t3;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}
function Message(t0) {
  const $ = _c(35);
  let message;
  let props;
  if ($[0] !== t0) {
    ({
      message,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = message;
    $[2] = props;
  } else {
    message = $[1];
    props = $[2];
  }
  const {
    t
  } = useDocsI18n();
  let markdown = "";
  let t1;
  if ($[3] !== t) {
    t1 = t("aiSearch.unknownRole");
    $[3] = t;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let roleLabel = t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[5] !== markdown || $[6] !== message.parts || $[7] !== message.role || $[8] !== props || $[9] !== roleLabel || $[10] !== t) {
    const searchCalls = [];
    if (message.role === "user") {
      let t7;
      if ($[18] !== t) {
        t7 = t("aiSearch.you");
        $[18] = t;
        $[19] = t7;
      } else {
        t7 = $[19];
      }
      roleLabel = t7;
    } else {
      if (message.role === "assistant") {
        let t7;
        if ($[20] !== t) {
          t7 = t("aiSearch.assistant");
          $[20] = t;
          $[21] = t7;
        } else {
          t7 = $[21];
        }
        roleLabel = t7;
      }
    }
    for (const part of message.parts ?? []) {
      if (part.type === "text") {
        markdown = markdown + part.text;
        continue;
      }
      if (part.type.startsWith("tool-")) {
        const toolName = part.type.slice(5);
        const p = part as UIToolInvocation<Tool>;
        if (toolName !== "search" || !p.toolCallId) {
          continue;
        }
        searchCalls.push(p);
      }
    }
    t2 = _temp2;
    t3 = props;
    const t7 = message.role === "assistant" && "text-fd-primary";
    let t8;
    if ($[22] !== t7) {
      t8 = cn("mb-1 text-sm font-medium text-fd-muted-foreground", t7);
      $[22] = t7;
      $[23] = t8;
    } else {
      t8 = $[23];
    }
    if ($[24] !== roleLabel || $[25] !== t8) {
      t4 = <p className={t8}>{roleLabel}</p>;
      $[24] = roleLabel;
      $[25] = t8;
      $[26] = t4;
    } else {
      t4 = $[26];
    }
    if ($[27] !== markdown) {
      t5 = <div className="prose text-sm"><Markdown text={markdown} /></div>;
      $[27] = markdown;
      $[28] = t5;
    } else {
      t5 = $[28];
    }
    t6 = searchCalls.map(call => <div key={call.toolCallId} className="flex flex-row gap-2 items-center mt-3 rounded-lg border bg-fd-secondary text-fd-muted-foreground text-xs p-2"><SearchIcon className="size-4" />{call.state === "output-error" || call.state === "output-denied" ? <p className="text-fd-error">{call.errorText ?? t("aiSearch.searchFailed")}</p> : <p>{!call.output ? t("aiSearch.searching") : t("aiSearch.searchResults", {
          count: call.output.length
        })}</p>}</div>);
    $[5] = markdown;
    $[6] = message.parts;
    $[7] = message.role;
    $[8] = props;
    $[9] = roleLabel;
    $[10] = t;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = roleLabel;
    $[17] = markdown;
  } else {
    t2 = $[11];
    t3 = $[12];
    t4 = $[13];
    t5 = $[14];
    t6 = $[15];
    roleLabel = $[16];
    markdown = $[17];
  }
  let t7;
  if ($[29] !== t2 || $[30] !== t3 || $[31] !== t4 || $[32] !== t5 || $[33] !== t6) {
    t7 = <div onClick={t2} {...t3}>{t4}{t5}{t6}</div>;
    $[29] = t2;
    $[30] = t3;
    $[31] = t4;
    $[32] = t5;
    $[33] = t6;
    $[34] = t7;
  } else {
    t7 = $[34];
  }
  return t7;
}
function _temp2(e) {
  return e.stopPropagation();
}
export function AISearch(t0) {
  const $ = _c(7);
  const {
    children
  } = t0;
  const [open, setOpen] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      id: "search",
      transport: new DefaultChatTransport({
        api: "/api/chat"
      })
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const chat = useChat(t1);
  let t2;
  if ($[1] !== chat || $[2] !== open) {
    t2 = {
      chat,
      open,
      setOpen
    };
    $[1] = chat;
    $[2] = open;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = t2;
  let t4;
  if ($[4] !== children || $[5] !== t3) {
    t4 = <Context value={t3}>{children}</Context>;
    $[4] = children;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
export function AISearchTrigger(t0) {
  const $ = _c(20);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      position: t1,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const position = t1 === undefined ? "default" : t1;
  const {
    open,
    setOpen
  } = useAISearchContext();
  const {
    t
  } = useDocsI18n();
  const t2 = open ? "open" : "closed";
  let t3;
  if ($[4] !== className || $[5] !== open || $[6] !== position) {
    t3 = cn(position === "float" && ["fixed bottom-4 gap-3 w-24 inset-e-[calc(--spacing(4)+var(--removed-body-scroll-bar-size,0px))] shadow-lg z-20 transition-[translate,opacity]", open && "translate-y-10 opacity-0"], className);
    $[4] = className;
    $[5] = open;
    $[6] = position;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== open || $[9] !== setOpen) {
    t4 = () => setOpen(!open);
    $[8] = open;
    $[9] = setOpen;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== props.children || $[12] !== t) {
    t5 = props.children ?? t("aiSearch.trigger");
    $[11] = props.children;
    $[12] = t;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== props || $[15] !== t2 || $[16] !== t3 || $[17] !== t4 || $[18] !== t5) {
    t6 = <button data-state={t2} className={t3} onClick={t4} {...props}>{t5}</button>;
    $[14] = props;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
}
export function AISearchTriggerContent() {
  const $ = _c(5);
  const {
    t
  } = useDocsI18n();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <MessageCircleIcon className="size-4.5" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== t) {
    t1 = t("aiSearch.trigger");
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <>{t0}{t1}</>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
export function AISearchPanel() {
  const $ = _c(25);
  const {
    open,
    setOpen
  } = useAISearchContext();
  useHotKey();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <style>{"\n        @keyframes ask-ai-open {\n          from {\n            translate: 100% 0;\n          }\n          to {\n            translate: 0 0;\n          }\n        }\n        @keyframes ask-ai-close {\n          from {\n            width: var(--ai-chat-width);\n          }\n          to {\n            width: 0px;\n          }\n        }"}</style>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = open ? "animate-fd-fade-in" : "animate-fd-fade-out";
  let t2;
  if ($[1] !== t1) {
    t2 = cn("fixed inset-0 z-30 backdrop-blur-xs bg-fd-overlay lg:hidden", t1);
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== setOpen) {
    t3 = () => setOpen(false);
    $[3] = setOpen;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <div className={t2} onClick={t3} />;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== open || $[9] !== t4) {
    t5 = <Presence present={open}>{t4}</Presence>;
    $[8] = open;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const t6 = open ? "animate-fd-dialog-in lg:animate-[ask-ai-open_200ms]" : "animate-fd-dialog-out lg:animate-[ask-ai-close_200ms]";
  let t7;
  if ($[11] !== t6) {
    t7 = cn("overflow-hidden z-30 bg-fd-card text-fd-card-foreground [--ai-chat-width:400px] 2xl:[--ai-chat-width:460px]", "max-lg:fixed max-lg:inset-x-2 max-lg:inset-y-4 max-lg:border max-lg:rounded-2xl max-lg:shadow-xl", "lg:sticky lg:top-0 lg:h-dvh lg:border-s lg:ms-auto lg:in-[#nd-docs-layout]:[grid-area:toc] lg:in-[#nd-notebook-layout]:row-span-full lg:in-[#nd-notebook-layout]:col-start-5", t6);
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <AISearchPanelHeader />;
    t9 = <AISearchPanelList className="flex-1" />;
    $[13] = t8;
    $[14] = t9;
  } else {
    t8 = $[13];
    t9 = $[14];
  }
  let t10;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <AISearchInput />;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="flex flex-col size-full p-2 lg:p-3 lg:w-(--ai-chat-width)">{t8}{t9}<div className="rounded-xl border bg-fd-secondary text-fd-secondary-foreground shadow-sm has-focus-visible:shadow-md">{t10}<div className="flex items-center gap-1.5 p-1 empty:hidden"><AISearchInputActions /></div></div></div>;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== t7) {
    t12 = <div className={t7}>{t11}</div>;
    $[17] = t7;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== open || $[20] !== t12) {
    t13 = <Presence present={open}>{t12}</Presence>;
    $[19] = open;
    $[20] = t12;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t13 || $[23] !== t5) {
    t14 = <>{t0}{t5}{t13}</>;
    $[22] = t13;
    $[23] = t5;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  return t14;
}
export function AISearchPanelList(t0) {
  const $ = _c(15);
  let className;
  let props;
  let style;
  if ($[0] !== t0) {
    ({
      className,
      style,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = style;
  } else {
    className = $[1];
    props = $[2];
    style = $[3];
  }
  const chat = useChatContext();
  const {
    t
  } = useDocsI18n();
  let t1;
  if ($[4] !== chat.error || $[5] !== chat.messages || $[6] !== className || $[7] !== props || $[8] !== style || $[9] !== t) {
    const messages = chat.messages.filter(_temp3);
    let t2;
    if ($[11] !== className) {
      t2 = cn("py-4 overscroll-contain", className);
      $[11] = className;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    let t3;
    if ($[13] !== style) {
      t3 = {
        maskImage: "linear-gradient(to bottom, transparent, white 1rem, white calc(100% - 1rem), transparent 100%)",
        ...style
      };
      $[13] = style;
      $[14] = t3;
    } else {
      t3 = $[14];
    }
    t1 = <List className={t2} style={t3} {...props}>{messages.length === 0 ? <div className="text-sm text-fd-muted-foreground/80 size-full flex flex-col items-center justify-center text-center gap-2"><MessageCircleIcon fill="currentColor" stroke="none" /><p onClick={_temp4}>{t("aiSearch.empty")}</p></div> : <div className="flex flex-col px-3 gap-4">{chat.error && <div className="p-2 bg-fd-secondary text-fd-secondary-foreground border rounded-lg"><p className="text-xs text-fd-muted-foreground mb-1">{t("aiSearch.requestFailed", {
              name: chat.error.name
            })}</p><p className="text-sm">{chat.error.message}</p></div>}{messages.map(_temp5)}</div>}</List>;
    $[4] = chat.error;
    $[5] = chat.messages;
    $[6] = className;
    $[7] = props;
    $[8] = style;
    $[9] = t;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  return t1;
}
function _temp5(item) {
  return <Message key={item.id} message={item} />;
}
function _temp4(e) {
  return e.stopPropagation();
}
function _temp3(msg) {
  return msg.role !== "system";
}
export function useHotKey() {
  const $ = _c(6);
  const {
    open,
    setOpen
  } = useAISearchContext();
  let t0;
  if ($[0] !== open || $[1] !== setOpen) {
    t0 = e => {
      if (e.key === "Escape" && open) {
        setOpen(false);
        e.preventDefault();
      }
      if (e.key === "/" && (e.metaKey || e.ctrlKey) && !open) {
        setOpen(true);
        e.preventDefault();
      }
    };
    $[0] = open;
    $[1] = setOpen;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const onKeyPress = useEffectEvent(t0);
  let t1;
  if ($[3] !== onKeyPress) {
    t1 = () => {
      window.addEventListener("keydown", onKeyPress);
      return () => window.removeEventListener("keydown", onKeyPress);
    };
    $[3] = onKeyPress;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  useEffect(t1, t2);
}
export function useAISearchContext() {
  return use(Context)!;
}
function useChatContext() {
  return use(Context)!.chat;
}